import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import clsx from "clsx";

import { makeRequest } from "../../utils/request";
import { Button, EmptyState, Spinner, Text, View } from "../../components/base";
import { Icon } from "../../components/base/Icon";
import { FineTuningPipelineStepper } from "../../components/FineTuningPipelineStepper";

// A released, agent-callable model (voicerun/<name>) pinned to an evaluated fine-tune. Each org is
// served in a single cluster, so `endpoints` (cluster name -> in-cluster serving URL) has one entry;
// it's keyed by cluster because different orgs live in different clusters.
type RegisteredModel = {
  id: string;
  name: string;
  status: string;
  org_id: string;
  base_model: string;
  model_uri: string;
  job_id: string | null;
  eval_id: string | null;
  deployment_id: string | null;
  endpoints: Record<string, string>;
  description: string | null;
  created_at: string;
  updated_at: string;
};

type ModelEvent = {
  id: string;
  type: string;
  level: string;
  message: string;
  created_at: string;
};

const POLL_MS = 6000;
const ALL_ORGS = "all_orgs=true";

const COLUMNS = "1fr 1.4fr 1.4fr 1.5fr 0.7fr 0.8fr 0.9fr";

const asUtc = (iso: string) => (/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);

const relativeTime = (iso: string | null): string => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(asUtc(iso)).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const clusters = (m: RegisteredModel) => Object.keys(m.endpoints || {});

// Serving-readiness, derived from the registry status + endpoints. `status` is the registry lifecycle
// (active/retired); a model is only actually callable once it has a live endpoint, so "active with no
// endpoints yet" reads as "provisioning" — otherwise a human sees "active" and tries to use it before
// the serving pod is up. (Agents are already guarded server-side: the ?cluster= list only returns
// models with an endpoint in that cluster.)
const servingState = (status: string, endpoints: Record<string, string>) => {
  if (status !== "active")
    return { label: "retired", title: "retired — no longer callable", cls: "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300" };
  if (!Object.keys(endpoints || {}).length)
    return {
      label: "provisioning",
      title: "registered; serving pod is still coming up — not callable yet",
      cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    };
  return { label: "serving", title: "live — callable at its endpoint(s)", cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" };
};

const StatusBadge = ({ status, endpoints }: { status: string; endpoints: Record<string, string> }) => {
  const s = servingState(status, endpoints);
  return (
    <span
      title={s.title}
      className={clsx("inline-block rounded-full px-2 py-0.5 text-xs font-medium", s.cls)}
    >
      {s.label}
    </span>
  );
};

export const AdminFineTuningModelsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedName = searchParams.get("name");

  const [models, setModels] = useState<RegisteredModel[]>([]);
  // GPU per model, pulled from its backing deployment (the registry doesn't store the accelerator).
  const [gpuByModel, setGpuByModel] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setIsLoading(true);
    try {
      type Dep = { org_id: string; served_model_name: string; accelerator: string; status: string };
      const [modelsRes, depsRes] = await Promise.all([
        makeRequest<{ data: RegisteredModel[] }>(`/v1/fine_tuning/models?${ALL_ORGS}&limit=200`),
        makeRequest<{ data: Dep[] }>(`/v1/fine_tuning/deployments?${ALL_ORGS}&limit=200`).catch(() => ({
          data: [] as Dep[],
        })),
      ]);
      setModels(modelsRes.data);
      // map (org|servedName) -> accelerator, preferring the running deployment
      const gpu: Record<string, string> = {};
      for (const d of depsRes.data) {
        const key = `${d.org_id}|${d.served_model_name}`;
        if (d.status === "running" || !(key in gpu)) gpu[key] = d.accelerator;
      }
      setGpuByModel(gpu);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load models");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
  }, [load]);

  // endpoints fill in as serving comes up in each cluster — keep polling while any model is active
  const hasActive = models.some((m) => m.status === "active");
  useEffect(() => {
    if (!hasActive) return;
    const t = setInterval(() => load(false), POLL_MS);
    return () => clearInterval(t);
  }, [hasActive, load]);

  const selected = models.find((m) => m.name === selectedName) || null;
  const gpuFor = (m: RegisteredModel) => gpuByModel[`${m.org_id}|${m.name}`] ?? "—";

  return (
    <View className="h-full w-full bg-gray-50 dark:bg-slate-900">
      <div className={clsx("h-full w-full relative transition-all duration-300", selectedName && "pr-[440px]")}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Text className="text-xl font-semibold">Fine-Tuning Models</Text>
            <Text className="text-sm text-gray-500 dark:text-gray-400">
              {models.length} {models.length === 1 ? "model" : "models"} · all orgs
            </Text>
          </div>
          <Button
            variant="ghost"
            type="button"
            onClick={() => load(true)}
          >
            <Icon
              name="refresh"
              size={18}
              className="text-brand"
            />
          </Button>
        </div>

        <FineTuningPipelineStepper />

        <div
          className="grid border-b-2 border-gray-200 dark:border-gray-700 px-6"
          style={{ gridTemplateColumns: COLUMNS }}
        >
          {["Org", "Model", "Base model", "Cluster", "GPU", "Status", "Released"].map((h) => (
            <span
              key={h}
              className="py-3 pr-2 text-sm font-semibold text-gray-700 dark:text-gray-200"
            >
              {h}
            </span>
          ))}
        </div>

        <div
          className="overflow-y-auto"
          style={{ height: "calc(100% - 110px)" }}
        >
          {isLoading ? (
            <View className="flex items-center justify-center py-20">
              <Spinner className="!text-brand" />
            </View>
          ) : error ? (
            <View className="flex items-center justify-center py-20">
              <Text className="text-error">{error}</Text>
            </View>
          ) : models.length === 0 ? (
            <EmptyState
              icon="rocket"
              title="No released models yet"
              description="Models released via `vr ft release` become agent-callable as voicerun/<name> and appear here, across all organizations."
            />
          ) : (
            models.map((m) => {
              const served = clusters(m);
              return (
                <div
                  key={m.id}
                  onClick={() => setSearchParams({ name: m.name })}
                  className={clsx(
                    "grid items-center px-6 border-b border-gray-100 dark:border-gray-800 cursor-pointer",
                    "hover:bg-gray-100 dark:hover:bg-gray-800",
                    selectedName === m.name && "bg-gray-100 dark:bg-gray-800",
                  )}
                  style={{ gridTemplateColumns: COLUMNS }}
                >
                  <span
                    className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400"
                    title={m.org_id}
                  >
                    {m.org_id}
                  </span>
                  <span
                    className="py-3 pr-2 truncate text-sm font-medium"
                    title={`voicerun/${m.name}`}
                  >
                    {m.name}
                  </span>
                  <span
                    className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400"
                    title={m.base_model}
                  >
                    {m.base_model}
                  </span>
                  <span
                    className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400"
                    title={served.join(", ")}
                  >
                    {served.length ? served.join(", ") : "—"}
                  </span>
                  <span
                    className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400"
                    title={gpuFor(m)}
                  >
                    {gpuFor(m)}
                  </span>
                  <span className="py-3 pr-2">
                    <StatusBadge
                      status={m.status}
                      endpoints={m.endpoints}
                    />
                  </span>
                  <span className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400">{relativeTime(m.created_at)}</span>
                </div>
              );
            })
          )}
        </div>
      </div>

      {selected && (
        <ModelDetailPanel
          model={selected}
          gpu={gpuFor(selected)}
          onClose={() => setSearchParams({})}
          onChanged={() => load(false)}
        />
      )}
    </View>
  );
};

type PanelProps = {
  model: RegisteredModel;
  gpu: string;
  onClose: () => void;
  onChanged: () => void;
};

const ModelDetailPanel = ({ model, gpu, onClose, onChanged }: PanelProps) => {
  const [events, setEvents] = useState<ModelEvent[]>([]);
  const [isRetiring, setIsRetiring] = useState(false);
  const nameRef = useRef(model.name);
  nameRef.current = model.name;

  const isRetired = model.status !== "active";
  const served = Object.entries(model.endpoints || {});

  const loadEvents = useCallback(async () => {
    try {
      const { data } = await makeRequest<{ data: ModelEvent[] }>(`/v1/fine_tuning/models/${encodeURIComponent(model.name)}/events?all_orgs=true`);
      if (nameRef.current === model.name) setEvents(data);
    } catch {
      /* best-effort */
    }
  }, [model.name]);

  useEffect(() => {
    setEvents([]);
    loadEvents();
    if (isRetired) return;
    const t = setInterval(loadEvents, POLL_MS);
    return () => clearInterval(t);
  }, [loadEvents, isRetired]);

  const onRetire = async () => {
    setIsRetiring(true);
    try {
      await makeRequest(`/v1/fine_tuning/models/${encodeURIComponent(model.name)}?all_orgs=true`, { method: "DELETE" });
      onChanged();
      loadEvents();
    } catch {
      /* surfaced via refresh */
    } finally {
      setIsRetiring(false);
    }
  };

  return (
    <div className="fixed right-0 top-0 h-screen w-[440px] bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col z-20">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Text className="text-base font-semibold">Model</Text>
          <StatusBadge
            status={model.status}
            endpoints={model.endpoints}
          />
        </div>
        <Button
          variant="ghost"
          type="button"
          onClick={onClose}
        >
          <Icon
            name="close"
            size={18}
            className="text-gray-500"
          />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        <Section
          label="Agent-callable name"
          value={`voicerun/${model.name}`}
          mono
        />
        <Section
          label="Organization"
          value={model.org_id}
          mono
        />
        <Field
          label="Base model"
          value={model.base_model || "—"}
        />
        <Field
          label="GPU"
          value={gpu}
        />

        <div>
          <Text className="block text-xs uppercase text-gray-400 mb-1">Cluster</Text>
          {served.length === 0 ? (
            <Text className="text-sm text-gray-400">Not serving in any cluster yet.</Text>
          ) : (
            <div className="space-y-2">
              {served.map(([cluster, url]) => (
                <div key={cluster}>
                  <Text className="block text-sm font-medium break-all">{cluster}</Text>
                  <Text className="block font-mono text-xs text-gray-500 dark:text-gray-400 break-all">{url}</Text>
                </div>
              ))}
            </div>
          )}
        </div>

        <Section
          label="Model URI"
          value={model.model_uri || "—"}
          mono
        />
        {model.job_id && (
          <Field
            label="Source job"
            value={model.job_id}
            mono
          />
        )}
        {model.eval_id && (
          <Field
            label="Eval"
            value={model.eval_id}
            mono
          />
        )}
        {model.deployment_id && (
          <div className="flex items-baseline justify-between gap-3">
            <Text className="text-xs uppercase text-gray-400 flex-shrink-0">Deployment</Text>
            <RouterLink
              to={`/admin/fine-tuning-deployments?id=${model.deployment_id}`}
              className="min-w-0 text-right"
              title="Open this deployment"
            >
              <Text className="break-all font-mono text-xs text-brand hover:underline">{model.deployment_id}</Text>
            </RouterLink>
          </div>
        )}
        {model.description && (
          <Section
            label="Description"
            value={model.description}
          />
        )}
        <Field
          label="Released"
          value={new Date(asUtc(model.created_at)).toLocaleString()}
        />
        <Field
          label="Updated"
          value={new Date(asUtc(model.updated_at)).toLocaleString()}
        />

        <div>
          <Text className="text-xs uppercase text-gray-400 mb-2">Events</Text>
          {events.length === 0 ? (
            <Text className="text-sm text-gray-400">No events yet.</Text>
          ) : (
            <div className="space-y-2">
              {events.map((e) => (
                <div
                  key={e.id}
                  className="flex gap-2 text-sm"
                >
                  <span
                    className={clsx(
                      "mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0",
                      e.level === "error" ? "bg-error" : e.level === "warning" ? "bg-yellow-500" : "bg-info",
                    )}
                  />
                  <div className="min-w-0">
                    <Text className="block text-gray-800 dark:text-gray-200 break-words">{e.message}</Text>
                    <Text className="block text-xs text-gray-400 mt-0.5">{relativeTime(e.created_at)}</Text>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {!isRetired && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            className="w-full"
            variant="ghost"
            type="button"
            onClick={onRetire}
            isLoading={isRetiring}
          >
            <Text className="text-error">Retire model</Text>
          </Button>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div className="flex items-baseline justify-between gap-3">
    <Text className="text-xs uppercase text-gray-400 flex-shrink-0">{label}</Text>
    <Text className={clsx("text-sm text-right break-words", mono && "font-mono text-xs")}>{value}</Text>
  </div>
);

const Section = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div>
    {/* the design-system Text is inline; `block` keeps the value on its own line under the label */}
    <Text className="block text-xs uppercase text-gray-400 mb-1">{label}</Text>
    <Text className={clsx("block text-sm break-all", mono && "font-mono text-xs")}>{value}</Text>
  </div>
);
